/**
 * Impact page — "overall industry baseline" math (DAT-8875).
 *
 * The Impact page bubble chart (IndustryPipelineOverview) already draws a
 * dashed crosshair at the mean of the bubbles it is CURRENTLY showing. When the
 * user scopes down (one industry / one UC / one asset) that mean describes the
 * subset only, so a PM can't tell whether the subset sits above or below the
 * whole population.
 *
 * The baseline is the SAME crosshair computed over the unscoped population at
 * the SAME grain: drop the industry / UC / asset scoping, keep the pipeline
 * property filter and the snapshot date. Keeping the property filter is
 * deliberate — the comparison should differ only in scope, not in what counts
 * as a pipeline. See `baselinePoint` for why the mean (not the total) is the
 * right reference: a per-item mean is grain-comparable, a total is not.
 *
 * Pure module on purpose — the canvas drawing and the fetch plumbing live in
 * IndustryPipelineOverview, this file holds the arithmetic so it's unit-tested
 * without a DOM or a client.
 */

import type { Bubble } from '../pages/UseCasesPage/components/PipelineMap';

/** Mean position of a bubble population, in data (not pixel) coordinates. */
export interface BaselinePoint {
  /** Mean of the current X-axis metric. */
  x: number;
  /** Mean of the current Y-axis metric. */
  y: number;
  /** How many bubbles the mean was taken over — surfaced in the legend so a
   *  baseline drawn from a thin population is visibly thin. */
  count: number;
}

/**
 * Mean X/Y over `bubbles`, using the caller's axis readers so the baseline
 * always tracks whatever the chart is currently plotting (Engagement Depth vs.
 * Realized-vs-Potential swap both axes).
 *
 * Ghost bubbles are excluded: they are all-zero placeholders for UCs with no
 * pipeline attached and are drawn in a separate column with their own scale, so
 * counting them would drag the baseline toward the origin by an amount that
 * depends on asset-tagging coverage rather than on pipeline reality. This
 * matches how `drawChartFigma` computes the existing scope mean (`dataBubbles`).
 *
 * @returns null when there is nothing to average — callers MUST treat null as
 *          "no baseline available" and skip drawing rather than plotting 0/0,
 *          which would read as a real reference at the origin.
 */
export function baselinePoint(
  bubbles: readonly Bubble[] | null | undefined,
  xOf: (b: Bubble) => number,
  yOf: (b: Bubble) => number,
): BaselinePoint | null {
  if (!bubbles) return null;
  const pop = bubbles.filter((b) => b.kind !== 'ghost');
  if (pop.length === 0) return null;
  let sx = 0;
  let sy = 0;
  for (const b of pop) {
    sx += xOf(b);
    sy += yOf(b);
  }
  return { x: sx / pop.length, y: sy / pop.length, count: pop.length };
}

/**
 * Legend/tooltip text for a baseline point. Y is money in Engagement Depth mode
 * and an open-opp count in Realized-vs-Potential mode, so the caller says which
 * — the same split `drawChartFigma` applies to its Y tick labels.
 *
 * `xIsInfluence` mirrors `AxisCfg.isInfluence`: influence densities are small
 * decimals and need 2dp, counts are integers.
 */
export function formatBaselinePoint(
  pt: BaselinePoint,
  opts: { yIsMoney: boolean; xIsInfluence: boolean },
): string {
  const y = opts.yIsMoney
    ? '$' + (pt.y / 1e6).toFixed(1) + 'M'
    : String(Math.round(pt.y));
  const x = opts.xIsInfluence ? pt.x.toFixed(2) : String(Math.round(pt.x));
  return `Y=${y}, X=${x}`;
}

/**
 * The legend chip's full text, including the non-value states.
 *
 * `baselinePoint` returns null for BOTH "still fetching" and "resolved, but the
 * population is empty" — an empty population is reachable whenever a restrictive
 * pipeline filter leaves no rows, or when every returned row is dropped by the
 * `totalArr > 0` guards in the bubble mappers. Collapsing those two into one
 * null is what made the chip sit on "(loading…)" forever, so the caller passes
 * the in-flight state explicitly and this function keeps the four cases apart.
 *
 * `loading` MUST mean "a request for the current key is genuinely in flight" —
 * not merely "we have no point". Views that need no fetch (Industry grain reads
 * the overview response) must pass false.
 */
export function baselineChipText(
  pt: BaselinePoint | null,
  opts: {
    grainSingular: string;
    yIsMoney: boolean;
    xIsInfluence: boolean;
    loading: boolean;
    failed: boolean;
  },
): string {
  if (pt) {
    const value = formatBaselinePoint(pt, {
      yIsMoney: opts.yIsMoney,
      xIsInfluence: opts.xIsInfluence,
    });
    return `avg ${opts.grainSingular}: ${value} · n=${pt.count}`;
  }
  if (opts.failed) return '(baseline unavailable)';
  if (opts.loading) return '(loading…)';
  return '(no baseline data)';
}
